import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AutoAwesome, Block, Cancel, CheckCircle, Sync, Visibility,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import { getApiErrorMessage } from '../../services/userService';
import { PayoutAccountRow, payoutApi } from '../../services/business/payoutService';
import {
  EMPTY_FILTERS, FilterState, PayoutFilterBar, PromptDialog, StatusChip, dateText, filterParams, personLabel, titleCase,
} from './payoutShared';
import { ActionIcon, ApprovalWarning, AutoCheckChip, AutoCheckPanel, suggestedReason } from './AutoCheckView';

const STATUS_TABS = ['pending', 'verified', 'rejected', 'disabled', ''];

const BANK_CODE_LABELS: Record<string, string> = {
  US: 'Routing', IN: 'IFSC', GB: 'Sort code', CA: 'Transit', AU: 'BSB', NG: 'CBN code', ZA: 'Branch code', GH: 'Sort code',
  ...Object.fromEntries(['KE', 'UG', 'TZ', 'RW', 'ET', 'ZM', 'BW', 'NA', 'MZ', 'DZ', 'SN', 'CI', 'CM'].map((c) => [c, 'SWIFT'])),
};
const bankCodeLabel = (country?: string) => BANK_CODE_LABELS[(country || 'US').toUpperCase()] || 'Bank code';

export default function PayoutAccountsPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || undefined;
  const [status, setStatus] = useState(userId ? '' : 'pending');
  const [type, setType] = useState('');
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [prompt, setPrompt] = useState<{ row: PayoutAccountRow; decision: 'verified' | 'rejected' | 'disabled' } | null>(null);
  const [checkRowId, setCheckRowId] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ row: PayoutAccountRow; accountNumber: string; routingNumber?: string; swiftCode?: string } | null>(null);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { search } = filterParams(filters);
  const params = { status: status || undefined, type: type || undefined, search, userId, page: page + 1, limit: pageSize };
  const { data, isLoading, error } = useQuery({
    queryKey: ['payout-accounts', params],
    queryFn: () => payoutApi.accounts(params),
    refetchInterval: (q) => (q.state.data?.rows.some((r) => r.autoCheck?.state === 'running') ? 4000 : false),
  });
  const checkRow = data?.rows.find((r) => r._id === checkRowId);

  const done = (text: string) => {
    setPrompt(null);
    setFlash({ type: 'success', text });
    queryClient.invalidateQueries({ queryKey: ['payout-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['payout-summary'] });
  };
  const fail = (e: unknown) => setFlash({ type: 'error', text: getApiErrorMessage(e) });

  const review = useMutation({
    mutationFn: (v: { id: string; decision: 'verified' | 'rejected' | 'disabled'; reason?: string; email?: string }) =>
      payoutApi.reviewAccount(v.id, v.decision, v.reason, v.email),
    onSuccess: (_r, v) => done(`Account ${v.decision}`),
    onError: fail,
  });
  const autoCheck = useMutation({
    mutationFn: (id: string) => { setChecking(id); return payoutApi.autoVerifyAccount(id); },
    onSuccess: (r) => setFlash({ type: r.recommendation === 'reject' ? 'error' : 'success', text: `Verification assistant: ${r.score}/100 — ${r.summary}` }),
    onError: fail,
    onSettled: () => {
      setChecking(null);
      queryClient.invalidateQueries({ queryKey: ['payout-accounts'] });
    },
  });
  const refresh = useMutation({ mutationFn: (id: string) => payoutApi.refreshAccount(id), onSuccess: () => done('Synced with Stripe'), onError: fail });
  const { data: settings } = useQuery({ queryKey: ['payout-settings'], queryFn: payoutApi.settings });
  const managedStripe = settings?.gateway === 'stripe' && !!settings.stripe?.managedBankPayouts;
  const paidViaStripe = (row: PayoutAccountRow) => row.type === 'bank'
    && (!!row.stripe?.managed || (managedStripe && ['US', 'IN'].includes(row.country || 'US')));
  const reveal = useMutation({
    mutationFn: (row: PayoutAccountRow) => payoutApi.revealAccount(row._id).then((r) => ({ row, ...r })),
    onSuccess: setRevealed,
    onError: fail,
  });

  const columns: GridColDef<PayoutAccountRow>[] = useMemo(() => [
    { field: 'createdAt', headerName: 'Added', width: 160, valueFormatter: (v) => dateText(v as string) },
    { field: 'user', headerName: 'Partner', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.user) },
    { field: 'userType', headerName: 'Type', width: 120, valueFormatter: (v) => titleCase(v as string) },
    {
      field: 'type', headerName: 'Method', width: 130,
      valueGetter: (_v, row) => (row.type === 'stripe_connect' ? 'Stripe Connect' : paidViaStripe(row) ? 'Bank (via Stripe)' : 'Bank (manual)'),
    },
    { field: 'accountHolderName', headerName: 'Holder', width: 150 },
    {
      field: 'bank', headerName: 'Bank', flex: 1, minWidth: 190,
      valueGetter: (_v, row) => [
        row.country, row.bankName, row.accountNumberMasked,
        row.routingNumber ? `${bankCodeLabel(row.country)} ${row.routingNumber}` : '', row.bankAccountType,
      ].filter(Boolean).join(' · '),
    },
    {
      field: 'flags', headerName: 'Checks', width: 190, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={0.5} alignItems="center" height="100%">
          {row.isPrimary && <Chip size="small" label="Primary" />}
          {row.isDuplicate && <Tooltip title="Same account number exists on another partner"><Chip size="small" color="error" label="Duplicate" /></Tooltip>}
          {row.type === 'stripe_connect' && (
            <Tooltip title={row.stripe?.requirementsDue?.length ? `Stripe needs: ${row.stripe.requirementsDue.join(', ')}` : 'Stripe onboarding complete'}>
              <Chip size="small" color={row.stripe?.payoutsEnabled ? 'success' : 'warning'} label={row.stripe?.payoutsEnabled ? 'Payouts on' : 'Onboarding'} />
            </Tooltip>
          )}
          {paidViaStripe(row) && (
            <Tooltip title={row.stripe?.setupError
              || (row.stripe?.payoutsEnabled ? 'Withdrawals are sent to this bank by Stripe'
                : row.stripe?.accountId ? 'Stripe is reviewing this recipient' : 'Registered with Stripe when you verify this bank account')}>
              <Chip
                size="small"
                color={row.stripe?.payoutsEnabled ? 'success' : row.stripe?.setupError ? 'error' : 'default'}
                label={row.stripe?.payoutsEnabled ? 'Stripe ready' : row.stripe?.setupError ? 'Stripe: action needed' : 'Stripe: not set up'}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: 'autoCheck', headerName: 'Assistant', width: 190, sortable: false,
      renderCell: ({ row }) => (
        <Stack height="100%" justifyContent="center" sx={{ cursor: 'pointer' }} onClick={() => setCheckRowId(row._id)}>
          <AutoCheckChip check={row.autoCheck} busy={checking === row._id} />
        </Stack>
      ),
    },
    { field: 'status', headerName: 'Status', width: 110, renderCell: ({ value }) => <StatusChip status={value as string} /> },
    {
      field: 'actions', headerName: 'Actions', width: 190, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" height="100%">
          {row.status !== 'verified' && (
            <ActionIcon title="Verify account" color="success" onClick={() => setPrompt({ row, decision: 'verified' })}><CheckCircle fontSize="small" /></ActionIcon>
          )}
          {row.status === 'pending' && (
            <ActionIcon title="Reject account" color="error" onClick={() => setPrompt({ row, decision: 'rejected' })}><Cancel fontSize="small" /></ActionIcon>
          )}
          {row.status === 'verified' && (
            <ActionIcon title="Disable account" color="error" onClick={() => setPrompt({ row, decision: 'disabled' })}><Block fontSize="small" /></ActionIcon>
          )}
          {row.type === 'bank' && (
            <ActionIcon title="Show full account number" color="primary" onClick={() => reveal.mutate(row)}><Visibility fontSize="small" /></ActionIcon>
          )}
          {paidViaStripe(row) && row.status === 'verified' && !row.stripe?.payoutsEnabled && (
            <ActionIcon title="Retry Stripe setup" color="warning" onClick={() => refresh.mutate(row._id)}><Sync fontSize="small" /></ActionIcon>
          )}
          {row.type === 'stripe_connect' && (
            <ActionIcon title="Sync with Stripe" color="primary" onClick={() => refresh.mutate(row._id)}><Sync fontSize="small" /></ActionIcon>
          )}
          <ActionIcon title="Run verification assistant" color="info" disabled={!!checking || row.autoCheck?.state === 'running'}
            onClick={() => { setCheckRowId(row._id); autoCheck.mutate(row._id); }}>
            <AutoAwesome fontSize="small" />
          </ActionIcon>
        </Stack>
      ),
    },
  ], [checking, managedStripe]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Partner Payout Accounts"
        subtitle="Bank accounts partners use for withdrawals. Manual accounts must be verified by an admin; Stripe accounts are verified by Stripe."
      />
      {flash && <Alert severity={flash.type} onClose={() => setFlash(null)} sx={{ mb: 2 }}>{flash.text}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error)}</Alert>}
      {userId && <Alert severity="info" sx={{ mb: 2 }}>Showing accounts of one partner.</Alert>}

      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {STATUS_TABS.map((s) => (
          <Chip key={s || 'all'} label={s ? titleCase(s) : 'All'} clickable
            color={status === s ? 'primary' : 'default'} variant={status === s ? 'filled' : 'outlined'}
            onClick={() => { setStatus(s); setPage(0); }} />
        ))}
      </Stack>
      <PayoutFilterBar
        value={filters}
        onChange={(f) => { setFilters(f); setPage(0); }}
        extra={(
          <TextField select size="small" label="Method" value={type} onChange={(e) => { setType(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
            <MenuItem value="">All methods</MenuItem>
            <MenuItem value="bank">Bank (manual)</MenuItem>
            <MenuItem value="stripe_connect">Stripe Connect</MenuItem>
          </TextField>
        )}
      />

      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[25, 50, 100]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <PromptDialog
        open={prompt?.decision === 'verified'}
        title={`Verify bank account — ${prompt?.row.accountHolderName || ''}`}
        description={<ApprovalWarning check={prompt?.row.autoCheck} subject="bank account" />}
        confirmLabel="Verify"
        confirmColor="success"
        fields={prompt && paidViaStripe(prompt.row) && !prompt.row.user?.email ? [{
          name: 'email', label: 'Partner email (required by Stripe — saved to their profile)', required: true, minLength: 5,
        }] : []}
        busy={review.isPending}
        onClose={() => setPrompt(null)}
        onSubmit={(v) => prompt && review.mutate({ id: prompt.row._id, decision: 'verified', email: v.email?.trim() || undefined })}
      />
      <PromptDialog
        open={prompt?.decision === 'rejected' || prompt?.decision === 'disabled'}
        title={prompt?.decision === 'disabled' ? 'Disable bank account' : 'Reject bank account'}
        confirmLabel={prompt?.decision === 'disabled' ? 'Disable' : 'Reject'}
        confirmColor="error"
        fields={[{
          name: 'reason', label: 'Reason shown to the partner', required: true, minLength: 3, type: 'multiline',
          defaultValue: suggestedReason(prompt?.row.autoCheck),
        }]}
        busy={review.isPending}
        onClose={() => setPrompt(null)}
        onSubmit={(v) => prompt && review.mutate({ id: prompt.row._id, decision: prompt.decision, reason: v.reason })}
      />

      <Dialog open={!!checkRowId} onClose={() => setCheckRowId(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {checkRow?.accountHolderName} · {checkRow?.bankName || (checkRow?.type === 'stripe_connect' ? 'Stripe Connect' : 'Bank')} {checkRow?.accountNumberMasked}
        </DialogTitle>
        <DialogContent dividers>
          {checkRow && <AutoCheckPanel check={checkRow.autoCheck} busy={checking === checkRow._id} onRun={() => autoCheck.mutate(checkRow._id)} />}
        </DialogContent>
        <DialogActions>
          {checkRow && checkRow.status !== 'verified' && (
            <Button color="success" startIcon={<CheckCircle />} onClick={() => { setPrompt({ row: checkRow, decision: 'verified' }); setCheckRowId(null); }}>Verify</Button>
          )}
          {checkRow?.status === 'pending' && (
            <Button color="error" startIcon={<Cancel />} onClick={() => { setPrompt({ row: checkRow, decision: 'rejected' }); setCheckRowId(null); }}>Reject</Button>
          )}
          <Button onClick={() => setCheckRowId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revealed} onClose={() => setRevealed(null)}>
        <DialogTitle>Bank details — {revealed?.row.accountHolderName}</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: 'monospace', fontSize: 16 }}>
            <Typography>Bank: {revealed?.row.bankName || '—'}</Typography>
            <Typography>Country: {revealed?.row.country || '—'}</Typography>
            {revealed?.routingNumber && <Typography>{bankCodeLabel(revealed.row.country)}: {revealed.routingNumber}</Typography>}
            <Typography>{revealed?.routingNumber ? 'Account' : 'IBAN / account'}: {revealed?.accountNumber}</Typography>
            {revealed?.swiftCode && <Typography>SWIFT: {revealed.swiftCode}</Typography>}
            <Typography>Type: {titleCase(revealed?.row.bankAccountType)}</Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>This view has been recorded in the payout audit log.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={() => setRevealed(null)}>Close</Button></DialogActions>
      </Dialog>
    </PageShell>
  );
}
